import { EditorContent } from '@tiptap/react';
import { forwardRef, useId, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { MarkdownToolbar } from '../MarkdownToolbar/MarkdownToolbar';
import { StructuredShell } from '../StructuredShell/StructuredShell';
import {
  MARKDOWN_HINT_CLASSES,
  MARKDOWN_ISLAND_CLASSES,
  MARKDOWN_ISLAND_DISABLED_CLASSES,
  MARKDOWN_ISLAND_INVALID_CLASSES,
  MARKDOWN_WARNING_CLASSES,
} from '../structured.tokens';

import { type MarkdownEditorHandle, type MarkdownEditorProps } from './MarkdownEditor.types';
import { useMarkdownEditorLogic } from './useMarkdownEditorLogic';

/**
 * `text-body`'s line height, so `minRows` means lines here as `rows` does on a
 * textarea. The one pixel figure in the family, because a contenteditable has
 * no `rows` to lean on.
 */
const BODY_LINE_HEIGHT_PX = 26;
const DEFAULT_MIN_ROWS = 8;

/**
 * A rich editor whose only formats are the ones the legal grammar carries —
 * so what staff see here is what the member's app draws (`MarkdownText`).
 *
 * The form holds markdown, the editor holds a document, and the logic hook is
 * the whole boundary between them. In read-only it is the reader as well: a
 * published version opens in this same control with the toolbar gone.
 */
export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  function MarkdownEditor(
    {
      id,
      className,
      label,
      'aria-describedby': describedBy,
      isDisabled = false,
      isReadOnly = false,
      isInvalid = false,
      onBlur,
      value,
      onValueChange,
      dir,
      minRows = DEFAULT_MIN_ROWS,
    },
    ref,
  ) {
    const { t } = useTranslation('uiKit');
    const generatedId = useId();
    const fieldId = id ?? generatedId;

    const logic = useMarkdownEditorLogic({
      value,
      onValueChange,
      onBlur,
      dir,
      fieldId,
      label,
      describedBy,
      isDisabled,
      isReadOnly,
      isInvalid,
    });

    useImperativeHandle(ref, () => ({ focus: logic.focus }), [logic.focus]);

    return (
      <StructuredShell
        className={className}
        label={label}
        isReadOnly={isReadOnly}
        announcement={logic.announcement}
        toolbar={
          isDisabled || isReadOnly ? undefined : (
            <MarkdownToolbar
              state={logic.toolbar}
              onToggleHeading={logic.toggleHeading}
              onToggleBold={logic.toggleBold}
              onToggleBulletList={logic.toggleBulletList}
              onToggleOrderedList={logic.toggleOrderedList}
              onUndo={logic.undo}
              onRedo={logic.redo}
            />
          )
        }
        footer={
          logic.hasDrift ? (
            <p role="status" className={MARKDOWN_WARNING_CLASSES}>
              {t('markdown.driftWarning')}
            </p>
          ) : (
            <p className={MARKDOWN_HINT_CLASSES}>{t('markdown.hint')}</p>
          )
        }
      >
        <div
          className={cn(
            MARKDOWN_ISLAND_CLASSES,
            isInvalid && MARKDOWN_ISLAND_INVALID_CLASSES,
            isDisabled && MARKDOWN_ISLAND_DISABLED_CLASSES,
          )}
          style={{ minHeight: `${minRows * BODY_LINE_HEIGHT_PX}px` }}
        >
          <EditorContent editor={logic.editor} className="flex grow" />
        </div>
      </StructuredShell>
    );
  },
);
